'use client';

import React from 'react';
import { FolderOpen, Loader2, Square, RotateCcw, FolderCheck, ArrowRight } from 'lucide-react';

interface FolderPickerHeroProps {
  isProcessing: boolean;
  isDone: boolean;
  folderName: string;
  onSelectFolder: () => void;
  onAbort: () => void;
  onReset: () => void;
}

export default function FolderPickerHero({
  isProcessing,
  isDone,
  folderName,
  onSelectFolder,
  onAbort,
  onReset,
}: FolderPickerHeroProps) {
  const isIdle = !isProcessing && !isDone;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header strip */}
      <div className="bg-primary px-6 py-4">
        <h1 className="text-primary-foreground text-xl font-semibold tracking-tight">
          Renomeação Automática de PDFs
        </h1>
        <p className="text-primary-foreground/70 text-sm mt-1">
          Selecione uma pasta com PDFs da Segurança Social. A aplicação extrai o nome do titular de cada documento e guarda cópias renomeadas na subpasta <code className="bg-primary-foreground/15 px-1.5 py-0.5 rounded text-xs font-mono">saida</code>.
        </p>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {/* Workflow steps */}
        <div className="flex flex-wrap items-center gap-2 mb-6 text-sm text-muted-foreground">
          {[
            { label: 'Selecionar pasta', icon: FolderOpen },
            { label: 'Ler PDFs', icon: null },
            { label: 'Extrair "Nome"', icon: null },
            { label: 'Guardar em "saida"', icon: FolderCheck },
          ].map((step, i) => (
            <React.Fragment key={`step-${i}`}>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-xs font-medium text-secondary-foreground">
                {step.icon && <step.icon size={12} />}
                {step.label}
              </div>
              {i < 3 && (
                <ArrowRight size={14} className="text-border shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Folder name display */}
        {folderName && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-muted rounded-lg border border-border">
            <FolderCheck size={16} className="text-accent shrink-0" />
            <span className="text-sm text-foreground font-medium truncate">
              {folderName}
            </span>
            <span className="text-xs text-muted-foreground ml-auto shrink-0">pasta selecionada</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-3">
          {!isProcessing && (
            <button
              onClick={onSelectFolder}
              disabled={isProcessing}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <FolderOpen size={16} />
              {isDone ? 'Processar outra pasta' : 'Selecionar Pasta'}
            </button>
          )}

          {isProcessing && (
            <button
              onClick={onAbort}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-error text-white text-sm font-semibold rounded-lg hover:bg-error/90 active:scale-95 transition-all duration-150 shadow-sm"
            >
              <Square size={14} />
              Interromper
            </button>
          )}

          {isProcessing && (
            <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-muted rounded-lg text-sm text-muted-foreground">
              <Loader2 size={15} className="animate-spin text-primary" />
              A processar PDFs...
            </div>
          )}

          {isDone && !isProcessing && (
            <button
              onClick={onReset}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground text-sm font-medium rounded-lg hover:bg-secondary/70 active:scale-95 transition-all duration-150"
            >
              <RotateCcw size={14} />
              Limpar resultados
            </button>
          )}
        </div>

        {/* Info note */}
        <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
          Os ficheiros originais <strong>não são alterados</strong>. São criadas cópias renomeadas na subpasta{' '}
          <code className="bg-muted px-1 py-0.5 rounded font-mono">saida</code> dentro da pasta selecionada.
          O browser pedirá permissão de leitura e escrita na pasta.
        </p>
      </div>
    </div>
  );
}